import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  ScanLine, 
  Camera, 
  Zap, 
  Volume2, 
  VolumeX,
  StopCircle,
  RotateCcw,
  CheckCircle,
  AlertTriangle
} from "lucide-react";
import { BarcodeScanner } from "@/components/BarcodeScanner";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";

interface ProductScannerPanelProps {
  onProductScanned: (barcode: string) => void;
  onClose?: () => void;
  isOpen: boolean;
}

export const ProductScannerPanel = ({ 
  onProductScanned, 
  onClose, 
  isOpen 
}: ProductScannerPanelProps) => {
  const [scanMode, setScanMode] = useState<'single' | 'batch'>('single');
  const [scannedItems, setScannedItems] = useState<Array<{
    barcode: string;
    timestamp: number;
    confidence?: number;
  }>>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [batchProgress, setBatchProgress] = useState(0);
  const [scanStrength, setScanStrength] = useState(0);
  const { toast } = useToast();

  const handleScanResult = (barcode: string) => {
    const newScan = {
      barcode,
      timestamp: Date.now(),
      confidence: Math.floor(Math.random() * 20) + 80 // Simulated confidence
    };

    setScannedItems(prev => [newScan, ...prev]);
    
    // Update batch progress
    if (scanMode === 'batch') {
      setBatchProgress(prev => Math.min(prev + 10, 100));
    }

    // Call parent callback
    onProductScanned(barcode);

    toast({
      title: "✅ Product Scanned",
      description: `Barcode: ${barcode}`,
      duration: 2000,
    });

    // Auto-close for single scan mode
    if (scanMode === 'single' && onClose) {
      setTimeout(() => {
        onClose();
      }, 1500);
    }
  };

  const startScanning = () => {
    setIsScanning(true);
    setScanStrength(0);
    if (scanMode === 'batch') {
      setBatchProgress(0);
    }
  };

  const stopScanning = () => {
    setIsScanning(false);
    setScanStrength(0);
  };

  const clearHistory = () => {
    setScannedItems([]);
    setBatchProgress(0);
  };

  const toggleScanMode = () => {
    setScanMode(prev => prev === 'single' ? 'batch' : 'single');
    clearHistory();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <ScanLine className="w-5 h-5" />
              Product Scanner
              <Badge variant={scanMode === 'batch' ? 'default' : 'secondary'}>
                {scanMode === 'batch' ? 'Batch Mode' : 'Single Scan'}
              </Badge>
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSoundEnabled(!soundEnabled)}
              >
                {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </Button>
              {onClose && (
                <Button variant="outline" size="sm" onClick={onClose}>
                  Close
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Scanner Controls */}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={toggleScanMode}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Zap className="w-4 h-4" />
              {scanMode === 'batch' ? 'Switch to Single' : 'Switch to Batch'}
            </Button>
            
            {scannedItems.length > 0 && (
              <Button
                onClick={clearHistory}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Clear History
              </Button>
            )}
          </div>

          {/* Batch Progress */}
          {scanMode === 'batch' && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Batch Progress</span>
                <span>{scannedItems.length} items scanned</span>
              </div>
              <Progress value={batchProgress} className="h-2" />
            </div>
          )}

          {/* Scanner Interface */}
          <div className="relative">
            {isScanning ? (
              <div className="space-y-4">
                <BarcodeScanner
                  onScanResult={handleScanResult}
                  onClose={stopScanning}
                />
                
                {/* Scan Strength Indicator */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Detection Strength</span>
                    <span>{scanStrength}%</span>
                  </div>
                  <Progress 
                    value={scanStrength} 
                    className={cn(
                      "h-2 transition-colors",
                      scanStrength > 70 ? "text-green-600" : 
                      scanStrength > 40 ? "text-yellow-600" : "text-red-600"
                    )}
                  />
                </div>

                <Button
                  onClick={stopScanning}
                  variant="destructive"
                  className="w-full flex items-center gap-2"
                >
                  <StopCircle className="w-4 h-4" />
                  Stop Scanning
                </Button>
              </div>
            ) : (
              <div className="text-center py-8 space-y-4">
                <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                  <Camera className="w-8 h-8 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-medium mb-2">Ready to Scan</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {scanMode === 'batch' 
                      ? 'Scan multiple products in sequence'
                      : 'Scan a single product barcode'
                    }
                  </p>
                </div>
                <Button
                  onClick={startScanning}
                  className="flex items-center gap-2"
                >
                  <ScanLine className="w-4 h-4" />
                  Start Scanning
                </Button>
              </div>
            )}
          </div>

          {/* Scan History */}
          {scannedItems.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Recent Scans</h4>
                <Badge variant="outline">{scannedItems.length} items</Badge>
              </div>
              
              <div className="max-h-40 overflow-y-auto space-y-2">
                {scannedItems.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-muted rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="font-mono text-sm">{item.barcode}</span>
                      </div>
                      {item.confidence && (
                        <Badge variant="outline" className="text-xs">
                          {item.confidence}%
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(item.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mobile Optimization Note */}
          <div className="text-xs text-muted-foreground bg-muted p-3 rounded-lg">
            💡 For best results: Hold device steady, ensure good lighting, and align barcode in center frame
          </div>
        </CardContent>
      </Card>
    </div>
  );
};